/*!
 * filters-sd.js – Haalt Search & Discovery filters en grid via Section Rendering API
 * - gebruikt server-side gemaakte links (value.url_to_add/url_to_remove)
 * - OR binnen groep, AND tussen groepen komt vanzelf goed via Shopify
 * - history.pushState + popstate
 */

(function () {
  'use strict';

  const cfg = {
    // IDs van sections die je opnieuw wil renderen
    filtersSectionId: null, // wordt dynamisch uit data-section-id gelezen
    gridSectionId: null, // vul zelf in (sectie die je productlisting rendert)

    // CSS selectors
    filtersRootSelector: '#filters-root',
    gridContainerSelector: '.pt-product-listing',
    paginationSelector: '[data-pagination], .pagination, .product-pagintion',

    // Loader (optioneel)
    loaderSelector: '.custom-loader',
  };

  let activeRefreshController = null;
  let activeRefreshId = 0;

  function $(sel, root = document) {
    return root.querySelector(sel);
  }
  function $all(sel, root = document) {
    return Array.from(root.querySelectorAll(sel));
  }

  function showLoader() {
    if (!cfg.loaderSelector) return;
    const el = $(cfg.loaderSelector);
    if (el) el.style.display = '';
  }
  function hideLoader() {
    if (!cfg.loaderSelector) return;
    const el = $(cfg.loaderSelector);
    if (el) el.style.display = 'none';
  }

  function setURL(url) {
    history.pushState({}, '', url);
  }

  function normalizeSectionId(sectionId) {
    if (typeof sectionId !== 'string') return sectionId;
    return sectionId.replace(/^section-/, '');
  }

  function buildSectionUrl(baseUrl, sectionId) {
    const u = new URL(baseUrl, window.location.origin);
    const normalizedSectionId = normalizeSectionId(sectionId);
    if (normalizedSectionId) {
      u.searchParams.set('section_id', normalizedSectionId);
    }
    return u.toString();
  }

  async function fetchSectionHTML(url, sectionId, signal) {
    const sectionURL = buildSectionUrl(url, sectionId);
    const fetchOptions = { credentials: 'same-origin' };
    if (signal) fetchOptions.signal = signal;
    const res = await fetch(sectionURL, fetchOptions);
    if (!res.ok) throw new Error('Section request failed: ' + res.status);
    return res.text();
  }

  function replaceElementFromDoc(docString, targetSelector) {
    const doc = new DOMParser().parseFromString(docString, 'text/html');
    const fresh = doc.querySelector(targetSelector);
    const target = $(targetSelector);
    if (target && fresh) target.replaceWith(fresh);
  }

  function replaceFiltersFromDoc(docString) {
    const doc = new DOMParser().parseFromString(docString, 'text/html');
    const fresh = doc.querySelector(cfg.filtersRootSelector);
    const target = document.querySelector(cfg.filtersRootSelector);
    if (!fresh || !target) return;

    const freshHasFilters = Boolean(
      fresh.querySelector('[data-action="toggle-filter"], .filter-group, .filters-active')
    );
    const currentHasFilters = Boolean(
      target.querySelector('[data-action="toggle-filter"], .filter-group, .filters-active')
    );

    // Bescherm tegen incidenteel lege responses: behoud huidige filters als de nieuwe payload leeg is.
    if (!freshHasFilters && currentHasFilters) return;

    target.replaceWith(fresh);
  }

  function replaceInnerFromDoc(docString, targetSelector) {
    const doc = new DOMParser().parseFromString(docString, 'text/html');
    const fresh = doc.querySelector(targetSelector);
    const target = document.querySelector(targetSelector);
    if (fresh && target) {
      target.innerHTML = fresh.innerHTML;
    }
  }

  async function refreshSections(url) {
    const refreshId = ++activeRefreshId;
    const canAbort = typeof AbortController !== 'undefined';
    if (canAbort && activeRefreshController) {
      activeRefreshController.abort();
    }
    activeRefreshController = canAbort ? new AbortController() : null;

    showLoader();
    try {
      const tasks = [];
      const sectionHTMLById = new Map();

      function getSectionHTML(sectionId) {
        const normalizedSectionId = normalizeSectionId(sectionId);
        if (!normalizedSectionId) return Promise.resolve('');
        if (!sectionHTMLById.has(normalizedSectionId)) {
          sectionHTMLById.set(
            normalizedSectionId,
            fetchSectionHTML(
              url,
              normalizedSectionId,
              activeRefreshController ? activeRefreshController.signal : undefined
            )
          );
        }
        return sectionHTMLById.get(normalizedSectionId);
      }

      if (cfg.filtersSectionId) {
        tasks.push(
          getSectionHTML(cfg.filtersSectionId).then((html) => {
            if (refreshId !== activeRefreshId) return;
            replaceFiltersFromDoc(html);
          })
        );
      }

      if (cfg.gridSectionId) {
        tasks.push(
          getSectionHTML(cfg.gridSectionId).then((html) => {
            if (refreshId !== activeRefreshId) return;
            replaceInnerFromDoc(html, cfg.gridContainerSelector);
          })
        );
      }

      await Promise.all(tasks);
      if (refreshId !== activeRefreshId) return;

      document.body.dispatchEvent(new CustomEvent('listing:ajax:done'));
    } catch (e) {
      if (e && e.name === 'AbortError') return;
      console.error(e);
      // Bij fout: full navigate (fallback)
      window.location.href = url;
    } finally {
      if (refreshId === activeRefreshId) hideLoader();
      // re-bind events op nieuw geplaatste DOM
      bindDelegates();
    }
  }

  function navigate(url) {
    setURL(url);
    refreshSections(url);
  }

  // ---- Event delegation ----
  let delegatedBound = false;
  function bindDelegates() {
    if (delegatedBound) return;
    delegatedBound = true;

    document.addEventListener('click', onClick, true);
    window.addEventListener('popstate', () => {
      refreshSections(location.href);
    });
  }

  function onClick(e) {
    const a = e.target.closest('a');
    if (!a) return;

    // Toggle een filter (server-side URL bepaalt wat er gebeurt)
    if (a.matches('[data-action="toggle-filter"], [data-action="remove-filter"]')) {
      const href = a.getAttribute('href');
      if (href && href !== '#') {
        e.preventDefault();
        navigate(href);
      }
      return;
    }

    // Alles wissen
    if (a.matches('[data-action="clear-all"], .filters-clear-all')) {
      const href = a.getAttribute('href') || window.location.pathname;
      e.preventDefault();
      navigate(href);
      return;
    }

    // Paginatie links
    if (a.closest(cfg.paginationSelector)) {
      const href = a.getAttribute('href');
      if (href) {
        e.preventDefault();
        navigate(href);
      }
      return;
    }
  }

  // ---- Init ----
  function init(options = {}) {
    Object.assign(cfg, options);

    // filtersSectionId uit DOM lezen
    const root = $(cfg.filtersRootSelector);
    if (root) {
      cfg.filtersSectionId = cfg.filtersSectionId || root.getAttribute('data-section-id');
    }

    if (!cfg.gridSectionId) {
      console.warn('[filters-sd] Tip: stel gridSectionId in zodat ook het productoverzicht ververst.');
    }

    bindDelegates();
  }

  // expose
  window.FiltersSD = { init };
})();
